import React, { Suspense, useState, useEffect, useRef, createRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import {
  createTheme,
  responsiveFontSizes,
  ThemeProvider,
} from '@mui/material/styles';
import shadows, { Shadows } from '@mui/material/styles/shadows';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { SnackbarProvider, SnackbarKey } from 'notistack';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';

import { meRequest } from 'redux/account/actions';
import { retrieveAllLOVRequest } from 'redux/lov/actions';
import { hideLoader, showLoader } from 'redux/loader/actions';
import { getLoaderState } from 'redux/loader/selectors';
import AppRouter from './AppRouter';
import AppLoader from './AppLoader';
import { AppSnackbar } from './AppSnackbar';

let theme = createTheme({
  shadows: Array(shadows.length).fill('none') as Shadows,
  typography: {
    fontFamily: `"Lato", "Helvetica", "Arial", sans-serif`,
  },
  palette: {
    primary: {
      main: '#b76daa',
      light: '#e3c9e4',
      dark: '#b76daa',
      // A0548D
    },
    secondary: {
      main: '#c293b4',
      light: '#c293b4',
      dark: '#a86e8e',
    },
  },
  shape: {
    borderRadius: '0px',
  },
  components: {
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          fontFamily: `"Lato", "Helvetica", "Arial", sans-serif`,
        },
      },
    },
  },
});
theme = responsiveFontSizes(theme);

const useSnackbarStyles = makeStyles({
  success: {
    backgroundColor: '#c293b4 !important',
    borderRadius: '0px !important',
    boxShadow: 'none !important',
  },
  info: {
    backgroundColor: '#c293b4 !important',
    borderRadius: '0px !important',
    boxShadow: 'none !important',
  },
  error: {
    backgroundColor: '#673c5f !important',
    borderRadius: '0px !important',
    boxShadow: 'none !important',
  },
  warning: {
    backgroundColor: '#673c5f !important',
    borderRadius: '0px !important',
    boxShadow: 'none !important',
  },
});

const EnvIndicator: React.FC = () => {
  const env = process.env.REACT_APP_ENV;

  return (
    <>
      {env !== 'prd' && (
        <Typography
          sx={{
            position: 'fixed',
            bottom: 6,
            right: 6,
            color: 'white',
            backgroundColor: 'red',
            p: '4px',
            borderRadius: '4px',
          }}
          variant="caption"
        >
          {env}
        </Typography>
      )}
    </>
  );
};

export const App: React.FC = () => {
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);
  const prevLocation = useRef(location);
  const dispatch = useDispatch();
  const notistackRef = createRef<SnackbarProvider>();
  const loaderShow = useSelector(getLoaderState);

  const snackbarClasses = useSnackbarStyles();

  // on mount
  useEffect(() => {
    window.scrollTo(0, 0);

    /**
     * Initializes the app
     */
    const init = async () => {
      dispatch(showLoader());

      await dispatch(meRequest());

      // Load all LOVs
      await dispatch(retrieveAllLOVRequest());

      setInitialized(true);
      dispatch(hideLoader());
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // on location change
  useEffect(() => {
    if (location.pathname !== prevLocation.current.pathname) {
      window.scrollTo(0, 0);
      prevLocation.current = location;
    }
  }, [location]);

  return (
    <Suspense fallback={null}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider
          hideIconVariant
          ref={notistackRef}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          classes={{
            variantSuccess: snackbarClasses.success,
            variantInfo: snackbarClasses.info,
            variantError: snackbarClasses.error,
            variantWarning: snackbarClasses.warning,
          }}
          action={(key: SnackbarKey) => (
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => {
                notistackRef?.current?.closeSnackbar(key);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <AppLoader show={loaderShow} />
            {initialized && <AppRouter />}
          </LocalizationProvider>
          <AppSnackbar />
        </SnackbarProvider>
      </ThemeProvider>
      <EnvIndicator />
    </Suspense>
  );
};
